import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast, Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  Database,
  Calendar,
  Bell,
  FileCheck,
  BarChart3,
  Columns,
  Info,
  CheckCircle,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import heartLoading from "../../../assests/heartLoading.gif";
import certifiedLogo from "../../../assests/certifiedLogo.png";
import AssetConfiguration from "../Domain/AssetConfiguration";
import {
  ProductOverviewTab,
  AssetOverviewTab,
  AssetSchemaTab,
  AssetProfilingTab,
  AssetDQRulesTab,
} from "../Dashboard/ProductViewComponents";
import { SLAandAlerts } from "./SLAandAlerts";

const PublishMain = ({ onPrevious, nextStep, id }) => {
  const navigate = useNavigate();
  const [selectedView, setSelectedView] = useState("product-overview");
  const [activeTab, setActiveTab] = useState("overview");
  const [sidebarWidth, setSidebarWidth] = useState(256);
  const [isResizing, setIsResizing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [publishData, setPublishData] = useState(null);
  const [selectedAssetData, setSelectedAssetData] = useState(null);
  const [assetLoading, setAssetLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const sidebarRef = useRef(null);
  const [isTablesOpen, setIsTablesOpen] = useState(true);
  const [isGeneralOpen, setIsGeneralOpen] = useState(true);

  // Fetch product overview data
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/product-overview-v2/${id}`)
      .then((res) => {
        setPublishData(res.data);
      })
      .catch(() => {
        setPublishData(null);
        toast.error("Failed to load publish data");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  // Handle sidebar resizing
  const startResizing = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      if (sidebarRef.current) {
        const sidebarRect = sidebarRef.current.getBoundingClientRect();
        const newWidth = e.clientX - sidebarRect.left;
        if (newWidth >= 200 && newWidth <= 400) {
          setSidebarWidth(newWidth);
        }
      }
    };

    const handleMouseUp = () => setIsResizing(false);

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  // Fetch asset details when an asset is selected
  const fetchAssetDetails = async (assetId) => {
    if (!assetId) return;

    setAssetLoading(true);
    setSelectedAssetData(null);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/asset-overview-v2/${assetId}`
      );
      setSelectedAssetData(response.data);
    } catch (error) {
      console.error("Error fetching asset details:", error);
      toast.error("Failed to load asset details");
      setSelectedAssetData(null);
    } finally {
      setAssetLoading(false);
    }
  };

  // Refresh publish data (for alert updates)
  const refreshPublishData = async () => {
    if (!id) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/product-overview-v2/${id}`
      );
      setPublishData(response.data);
    } catch (error) {
      console.error("Error refreshing publish data:", error);
    }
  };

  const handlePublish = async () => {
    if (!confirmationChecked) {
      toast.error(
        "Please confirm that you have reviewed all details before publishing."
      );
      return;
    }

    if (!id) return;
    setPublishing(true);
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/publish/${id}`);
      toast.success("Publish successful!");
      setTimeout(() => {
        navigate("/Dashboard");
      }, 1200);
    } catch (err) {
      toast.error("Publish failed. Please try again.");
    } finally {
      setPublishing(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center z-50 w-full h-full">
        <div className="flex flex-col items-center space-y-3">
          <img src={heartLoading} alt="Loading..." className="h-30" />
          <span className="text-gray-700 text-[20px]">
            Hang tight! The resources are loading...
          </span>
        </div>
      </div>
    );
  }

  // No data state
  if (!publishData || !publishData.ProductInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 text-lg">No product data available.</p>
        </div>
      </div>
    );
  }

  // Extract data from publishData
  const productInfo = publishData?.ProductInfo || {};
  const slaAndAlerts = publishData?.SLAandAlerts || {};
  const assetList = publishData?.AssetList || [];

  // Map AssetList to simplified array for sidebar display
  const assetsArray = assetList.map((asset) => ({
    id: asset.id,
    name: asset.AssetName || "N/A",
  }));

  const productOverview = {
    productName: productInfo.productName || "N/A",
    productCode: productInfo.productCode || "N/A",
    description: productInfo.description || "N/A",
    productOwner: productInfo.productOwner || "N/A",
    parentDomainName: productInfo.parentDomainName || "N/A",
    domainName: productInfo.domainName || "N/A",
    subDomainName: productInfo.subDomainName || "N/A",
    dataLayer: productInfo.dataLayer || "N/A",
    productTag: productInfo.productTag || [],
    productDocumentationUrl: productInfo.productDocumentationUrl || "N/A",
    status: productInfo.status || "N/A",
    marketRegion: productInfo.marketRegion || [],
    marketCountry: productInfo.marketCountry || [],
    lineOfBusiness: productInfo.lineOfBusiness || "N/A",
    minExpectedUsers: productInfo.minExpectedUsers || "N/A",
    maxExpectedUsers: productInfo.maxExpectedUsers || "N/A",
    assetSource: productInfo.assetSource || "N/A",
    sizeThreshold: productInfo.sizeThreshold || null,
    valueMapping: productInfo.valueMapping || "N/A",
    reportDetails: productInfo.reportDetails || [],
  };

  // Get the current selected asset from sidebar
  const currentAssetFromList =
    typeof selectedView === "number" ? assetsArray[selectedView] : null;

  // Build currentAsset from fetched data for use in tab components
  const currentAsset = selectedAssetData
    ? {
        key: currentAssetFromList?.id,
        name:
          selectedAssetData.Configuration?.assetName ||
          currentAssetFromList?.name,
        dataAssetOwner:
          selectedAssetData.Configuration?.dataAssetOwner || "N/A",
        Configuration: selectedAssetData.Configuration || {},
        tableDetails: selectedAssetData.tableDetails || [],
        ProfilingInfo: selectedAssetData.ProfilingInfo || [],
        DQInfo: selectedAssetData["DQ Info"] || {},
      }
    : null;

  const tabs = [
    { id: "overview", label: "Overview", icon: Database },
    { id: "schema", label: "Schema", icon: Columns },
    { id: "schedule", label: "Schedule", icon: Calendar },
    { id: "profiling", label: "Profiling", icon: BarChart3 },
    { id: "dq-rules", label: "DQ Rules", icon: FileCheck },
  ];

  return (
    <div className="h-[85vh] bg-gray-50 flex overflow-hidden relative">
      <Toaster position="top-right" />

      {/* Left Sidebar Navigation */}
      <div
        ref={sidebarRef}
        className="bg-white border-r border-gray-200 flex flex-col h-full flex-shrink-0"
        style={{ width: `${sidebarWidth}px` }}
      >
        {/* General Sections */}
        <div className="p-4 border-b border-gray-200">
          <button
            onClick={() => setIsGeneralOpen((prev) => !prev)}
            className="w-full flex items-center justify-between"
          >
            <h3 className="text-xs font-semibold text-gray-500 uppercase ">
              General
            </h3>
            {isGeneralOpen ? (
              <ChevronDown className="h-4 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 text-gray-500" />
            )}
          </button>

          {isGeneralOpen && (
            <div className="flex flex-col items-center">
              <button
                onClick={() => setSelectedView("product-overview")}
                className={`w-full text-left p-3 rounded-lg mb-2 transition-colors flex items-center gap-2 ${
                  selectedView === "product-overview"
                    ? "bg-blue-50 border border-blue-200 text-blue-900"
                    : "hover:bg-gray-50 border border-transparent text-gray-700"
                }`}
              >
                <Info className="h-4" />
                <span className="text-sm font-medium">Product Overview</span>
              </button>
              <button
                onClick={() => setSelectedView("notifications")}
                className={`w-full text-left p-3 rounded-lg transition-colors flex items-center gap-2 ${
                  selectedView === "notifications"
                    ? "bg-blue-50 border border-blue-200 text-blue-900"
                    : "hover:bg-gray-50 border border-transparent text-gray-700"
                }`}
              >
                <Bell className="h-4" />
                <span className="text-sm font-medium">Notifications</span>
              </button>
            </div>
          )}
        </div>

        {/* Tables Section */}
        <div className="px-4  border-b border-gray-200">
          <button
            onClick={() => setIsTablesOpen((prev) => !prev)}
            className="w-full flex items-center justify-between "
          >
            <h3 className="text-xs font-semibold text-gray-500 uppercase">
              Tables ({assetsArray.length})
            </h3>

            {isTablesOpen ? (
              <ChevronDown className="h-4 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 text-gray-500" />
            )}
          </button>

          {isTablesOpen && (
            <div className="max-h-[calc(100vh-550px)] overflow-y-auto">
              {assetsArray.map((asset, index) => (
                <button
                  key={asset.id}
                  onClick={() => {
                    setSelectedView(index);
                    setActiveTab("overview");
                    fetchAssetDetails(asset.id);
                  }}
                  className={`w-full text-left p-3 rounded-lg mb-2 transition-colors overflow-hidden ${
                    selectedView === index
                      ? "bg-blue-50 border border-blue-200"
                      : "hover:bg-gray-50 border border-transparent"
                  }`}
                >
                  <span
                    className={`text-sm font-medium block truncate ${
                      selectedView === index ? "text-blue-900" : "text-gray-900"
                    }`}
                    title={asset.name}
                  >
                    {asset.name}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Publish Section */}
        <div className="p-4">
          {/* <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">
            Publish
          </h3> */}
          <button
            onClick={() => setSelectedView("publish")}
            className={`w-full text-left p-3 rounded-lg transition-colors flex items-center gap-2 ${
              selectedView === "publish"
                ? "bg-blue-50 border border-blue-200 text-blue-900"
                : "hover:bg-gray-50 border border-transparent text-gray-700"
            }`}
          >
            <CheckCircle className="h-4" />
            <span className="text-sm font-medium">Publish</span>
          </button>
        </div>
      </div>

      {/* Resize Handle */}
      <div
        onMouseDown={startResizing}
        className="flex-shrink-0 cursor-col-resize group h-full"
        style={{ width: "8px" }}
      >
        <div
          className={`w-1 h-full bg-gray-300 group-hover:bg-blue-500 transition-colors duration-200 mx-auto ${
            isResizing ? "bg-blue-500" : ""
          }`}
        ></div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Publish Product
              </h1>
              <p className="text-gray-600 mt-1">
                Review all details before publishing your data product
              </p>
            </div>
            <div className="bg-yellow-100 text-yellow-700 px-4 py-2 rounded-full text-sm font-medium">
              Review Mode
            </div>
          </div>
        </div>

        {/* Product Overview Content */}
        {selectedView === "product-overview" && (
          <ProductOverviewTab productOverview={productOverview} />
        )}

        {/* Notifications Content with Alert Management */}
        {selectedView === "notifications" && (
          <div className="flex-1 overflow-y-auto p-6">
            <SLAandAlerts
              publishData={publishData}
              onDataRefresh={refreshPublishData}
              showProductSLA={true}
            />
          </div>
        )}

        {/* Publish Section */}
        {selectedView === "publish" && (
          <div className="flex-1 overflow-y-auto p-6">
            <div className="flex flex-col items-center justify-center h-full">
              <div className="max-w-2xl text-center">
                <div className="mb-4">
                  <div className="text-4xl mb-2 text-center flex justify-center">
                    <img
                      src={certifiedLogo}
                      alt="certifiedLogo"
                      className="h-[65px]"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">
                    Ready to Publish
                  </h3>
                  <p className="text-gray-600 mb-4">
                    You're about to publish your data product. Please review all
                    the information in the previous sections to ensure
                    everything is correct.
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 className="text-base font-semibold text-blue-800 mb-3">
                    Publishing Checklist
                  </h4>
                  <div className="space-y-2 text-left">
                    <div className="flex items-center space-x-3">
                      <div className="h-[10px] w-[10px] bg-green-100 border-2 border-green-500 rounded-full flex items-center justify-center">
                        <div className="w-[10px] h-[10px] bg-green-500 rounded-full"></div>
                      </div>
                      <span className="text-sm text-gray-700">
                        Product Details configured
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="h-[10px] w-[10px] bg-green-100 border-2 border-green-500 rounded-full flex items-center justify-center">
                        <div className="w-[10px] h-[10px] bg-green-500 rounded-full"></div>
                      </div>
                      <span className="text-sm text-gray-700">
                        Assets onboarded and profiled
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="h-[10px] w-[10px] bg-green-100 border-2 border-green-500 rounded-full flex items-center justify-center">
                        <div className="w-[10px] h-[10px] bg-green-500 rounded-full"></div>
                      </div>
                      <span className="text-sm text-gray-700">
                        Data Quality rules configured
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="h-[10px] w-[10px] bg-green-100 border-2 border-green-500 rounded-full flex items-center justify-center">
                        <div className="w-[10px] h-[10px] bg-green-500 rounded-full"></div>
                      </div>
                      <span className="text-sm text-gray-700">
                        SLA and Alerts configured
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="confirmation"
                      checked={confirmationChecked}
                      onChange={(e) => setConfirmationChecked(e.target.checked)}
                      className="mt-[8px] text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label
                      htmlFor="confirmation"
                      className="text-left text-gray-700 cursor-pointer"
                    >
                      <span className="font-medium text-yellow-800 text-sm">
                        I confirm that I have reviewed all the details above
                      </span>
                      <br />
                      <span className="text-xs text-gray-600">
                        I understand that once published, the data product will
                        be available to users and changes may require a new
                        version.
                      </span>
                    </label>
                  </div>
                </div>

                <button
                  className={`px-6 py-3 text-base font-bold rounded-lg shadow-lg transition-all duration-200 ${
                    confirmationChecked
                      ? "bg-[#bb1ccc] hover:bg-[#000080] text-white"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                  onClick={handlePublish}
                  disabled={publishing || !id || !confirmationChecked}
                >
                  {publishing ? "Publishing..." : "Publish Data Product"}
                </button>

                {!confirmationChecked && (
                  <p className="text-xs text-gray-500 mt-2">
                    Please check the confirmation box above to proceed with
                    publishing.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Table-specific content */}
        {typeof selectedView === "number" && (
          <>
            {/* Loading State for Asset Details */}
            {assetLoading && (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <img
                    src={heartLoading}
                    alt="Loading..."
                    className="w-20 h-20 mx-auto mb-4"
                  />
                  <p className="text-gray-600">Loading asset details...</p>
                </div>
              </div>
            )}

            {/* Asset Content when loaded */}
            {!assetLoading && currentAsset && (
              <>
                {/* Current Table Info */}
                <div className="bg-white border-b border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">
                        {currentAsset.Configuration?.assetName ||
                          currentAsset.name}
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        {currentAsset.Configuration?.assetDescription || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <div className="bg-white border-b border-gray-200 px-6">
                  <div className="flex space-x-1 overflow-x-auto">
                    {tabs.map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                            activeTab === tab.id
                              ? "text-blue-600 border-b-2 border-blue-600"
                              : "text-gray-600 hover:text-gray-900"
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  {activeTab === "overview" && (
                    <AssetOverviewTab currentAsset={currentAsset} />
                  )}
                  {activeTab === "schema" && (
                    <AssetSchemaTab currentAsset={currentAsset} />
                  )}
                  {activeTab === "schedule" && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <AssetConfiguration
                        assetOverview={currentAsset.Configuration}
                      />
                    </div>
                  )}
                  {activeTab === "profiling" && (
                    <AssetProfilingTab currentAsset={currentAsset} />
                  )}
                  {activeTab === "dq-rules" && (
                    <AssetDQRulesTab currentAsset={currentAsset} />
                  )}
                </div>
              </>
            )}

            {/* No asset data state */}
            {!assetLoading && !currentAsset && (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <p>Failed to load asset details. Please try again.</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PublishMain;
